import {
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAccount, useMsal } from '@azure/msal-react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Collapse,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  ThemeProvider,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowLeft,
  ChevronDown,
  LogOut,
  PanelLeft,
  RefreshCw,
} from 'lucide-react';

import RotraLogo from '$components/RotraLogo.jsx';
import useEnvironment from '$utils/useEnvironment';
import { VERSION_NUMBER } from '$utils/VersionUtils';

import { useApiAuth } from '../../auth/ApiAuthProvider';

import { Stack } from './components/Stack.jsx';
import { activeNavTo, NAV_SECTIONS } from './nav.jsx';
import { adminTheme } from './theme.js';

const EXPANDED = 248;
const COLLAPSED = 76;
const STORAGE_KEY = 'rotra.admin.sidebar';
const SECTION_STORAGE_KEY = 'rotra.admin.sidebar.section';

const sectionKey = (heading: string | undefined, index: number) =>
  heading ?? `section-${index}`;

const getInitials = (name: string) => {
  return name
    .split(' ')
    .slice(0, 2)
    .map((val) => `${val}`[0])
    .join('')
    .toUpperCase();
};

function useProfilePhoto() {
  const { instance, accounts } = useMsal();
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const accountId =
    instance.getActiveAccount()?.homeAccountId ?? accounts[0]?.homeAccountId;

  useEffect(() => {
    let cancelled = false;
    let nextPhotoUrl: string | null = null;
    const controller = new AbortController();
    const account =
      instance.getActiveAccount() ??
      instance
        .getAllAccounts()
        .find((candidate) => candidate.homeAccountId === accountId);

    if (!account || !accountId) {
      setPhotoUrl(null);
      return () => controller.abort();
    }

    void instance
      .acquireTokenSilent({
        scopes: ['User.Read'],
        account,
      })
      .then((res) =>
        fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
          headers: { Authorization: `Bearer ${res.accessToken}` },
          signal: controller.signal,
        }),
      )
      .then((res) => {
        if (!res.ok) {
          throw new Error('No photo');
        }
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) {
          return;
        }
        nextPhotoUrl = URL.createObjectURL(blob);
        setPhotoUrl(nextPhotoUrl);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      controller.abort();
      if (nextPhotoUrl) {
        URL.revokeObjectURL(nextPhotoUrl);
      }
    };
  }, [instance, accountId]);

  return photoUrl;
}

/** Shared chrome for the admin panel — sidebar nav, top bar, content region. */
export function AdminShell({
  title,
  titleIcon,
  actions,
  children,
  backAction,
}: {
  title: string;
  titleIcon?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  backAction?: string;
}) {
  const { pathname } = useLocation();
  const account = useAccount();
  const { instance } = useMsal();
  const photoUrl = useProfilePhoto();
  const navigate = useNavigate();
  const environment = useEnvironment();
  const [profileMenuAnchor, setProfileMenuAnchor] =
    useState<HTMLElement | null>(null);
  const { session, busy: refreshingSession } = useApiAuth();

  const refreshSession = async () => {
    setProfileMenuAnchor(null);
    try {
      await session.getToken(true);
    } catch {
      // The shared session dialog provides retry and sign-in recovery.
    }
  };

  const logout = () => {
    setProfileMenuAnchor(null);
    void instance.logoutRedirect({
      account: account ?? undefined,
    });
  };

  const [open, setOpen] = useState(
    () => localStorage.getItem(STORAGE_KEY) !== 'closed',
  );
  const [collapsedMenuAnchor, setCollapsedMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [collapsedMenuSection, setCollapsedMenuSection] = useState<
    string | null
  >(null);
  const toggle = () => {
    setCollapsedMenuAnchor(null);
    setCollapsedMenuSection(null);
    setOpen((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY, next ? 'open' : 'closed');
      return next;
    });
  };

  const width = open ? EXPANDED : COLLAPSED;
  const activeTo = activeNavTo(pathname);
  const dashboardItem = NAV_SECTIONS[0]?.items[0];
  const DashboardIcon = dashboardItem?.icon;
  const navScrollRef = useRef<HTMLDivElement>(null);
  const collapsedSection = NAV_SECTIONS.find(
    (section, index) =>
      sectionKey(section.heading, index) === collapsedMenuSection,
  );
  const CollapsedSectionIcon = collapsedSection?.icon;
  const activeSection = NAV_SECTIONS.findIndex((section) =>
    section.items.some((item) => item.to === activeTo),
  );
  const [openSection, setOpenSection] = useState<string | null>(() => {
    const stored = localStorage.getItem(SECTION_STORAGE_KEY);
    return stored || sectionKey(NAV_SECTIONS[0]?.heading, 0);
  });

  useEffect(() => {
    if (activeSection === -1) {
      return;
    }
    setOpenSection(
      sectionKey(NAV_SECTIONS[activeSection].heading, activeSection),
    );
    navScrollRef.current?.scrollTo({ top: 0 });
    setCollapsedMenuAnchor(null);
    setCollapsedMenuSection(null);
  }, [activeSection, pathname]);

  useEffect(() => {
    if (openSection) {
      localStorage.setItem(SECTION_STORAGE_KEY, openSection);
    } else {
      localStorage.removeItem(SECTION_STORAGE_KEY);
    }
  }, [openSection]);

  const toggleSection = (key: string) => {
    setOpenSection((current) => (current === key ? null : key));
  };

  const handleSectionClick = (
    event: ReactMouseEvent<HTMLElement>,
    key: string,
  ) => {
    if (!open) {
      setCollapsedMenuAnchor(event.currentTarget);
      setCollapsedMenuSection(key);
      return;
    }
    toggleSection(key);
  };

  return (
    <ThemeProvider theme={adminTheme}>
      <CssBaseline />
      <Box className="gh-dark" sx={{ display: 'flex', minHeight: '100vh' }}>
        <Drawer
          variant="permanent"
          sx={{
            width,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            '& .MuiDrawer-paper': {
              width,
              overflowX: 'hidden',
              borderRight: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              transition: (t) =>
                t.transitions.create('width', {
                  easing: t.transitions.easing.sharp,
                  duration: t.transitions.duration.enteringScreen,
                }),
            },
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              px: open ? 2 : 0,
              justifyContent: open ? 'space-between' : 'center',
              minHeight: 52,
            }}
          >
            {open && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 38,
                    height: 28,
                    borderRadius: 1.5,
                    display: 'grid',
                    placeItems: 'center',
                    color: '#fff',
                    bgcolor: '#1f2328',
                    '& svg': {
                      width: 28,
                      position: 'relative',
                      marginTop: -0.8,
                      marginLeft: 0.7,
                    },
                  }}
                >
                  <RotraLogo />
                </Box>
                <Typography sx={{ fontWeight: 700, letterSpacing: -0.2 }}>
                  rotraNext Admin
                </Typography>
              </Stack>
            )}
            <Tooltip title={open ? 'Collapse' : 'Expand'} placement="right">
              <IconButton onClick={toggle} size="small">
                <PanelLeft size={18} />
              </IconButton>
            </Tooltip>
          </Toolbar>
          <Divider />

          {dashboardItem && (
            <Box sx={{ p: 1 }}>
              <Tooltip
                title={open ? '' : dashboardItem.label}
                placement="right"
                disableHoverListener={open}
              >
                <ListItemButton
                  component={RouterLink}
                  to={dashboardItem.to}
                  selected={activeTo === dashboardItem.to}
                  sx={{
                    minHeight: 38,
                    borderRadius: 1.5,
                    justifyContent: open ? 'initial' : 'center',
                    px: 1.5,
                    '&.Mui-selected': {
                      bgcolor: 'action.selected',
                      '&:hover': { bgcolor: 'action.selected' },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 1.5 : 'auto',
                      justifyContent: 'center',
                      color:
                        activeTo === dashboardItem.to
                          ? 'secondary.main'
                          : 'text.secondary',
                    }}
                  >
                    {DashboardIcon && <DashboardIcon size={18} />}
                  </ListItemIcon>
                  {open && <ListItemText primary={dashboardItem.label} />}
                </ListItemButton>
              </Tooltip>
            </Box>
          )}
          <Divider />

          <Box
            ref={navScrollRef}
            sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden' }}
          >
            {NAV_SECTIONS.map((section, si) => {
              const SectionIcon = section.icon;
              const key = sectionKey(section.heading, si);
              return (
                <List
                  key={section.heading ?? si}
                  dense
                  disablePadding
                  sx={{ px: 1, pt: si === 0 ? 1 : 0.5, pb: 0.5 }}
                >
                  {section.heading && (
                    <Tooltip
                      title={open ? '' : section.heading}
                      placement="right"
                      disableHoverListener={open}
                    >
                      <ListItemButton
                        onClick={(event) => handleSectionClick(event, key)}
                        selected={!open && activeSection === si}
                        aria-expanded={
                          open
                            ? openSection === key
                            : collapsedMenuSection === key
                        }
                        sx={{
                          minHeight: open ? 32 : 44,
                          borderRadius: 1.5,
                          justifyContent: open ? 'initial' : 'center',
                          px: open ? 1.25 : 1.5,
                          color: 'text.secondary',
                          '&:hover': { bgcolor: 'action.hover' },
                          '&.Mui-selected': {
                            bgcolor: 'action.selected',
                            '&:hover': { bgcolor: 'action.selected' },
                            color: 'secondary.main',
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 1.25 : 0,
                            justifyContent: 'center',
                            color: 'inherit',
                          }}
                        >
                          <SectionIcon size={16} />
                        </ListItemIcon>
                        {open && (
                          <>
                            <ListItemText
                              primary={section.heading}
                              slotProps={{
                                primary: {
                                  sx: {
                                    fontSize: 11,
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.6,
                                  },
                                },
                              }}
                            />
                            <ChevronDown
                              size={15}
                              style={{
                                transform:
                                  openSection === key
                                    ? 'rotate(0deg)'
                                    : 'rotate(-90deg)',
                                transition: 'transform 160ms ease',
                              }}
                            />
                          </>
                        )}
                      </ListItemButton>
                    </Tooltip>
                  )}
                  <Collapse
                    in={open && openSection === key}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box
                      sx={{
                        ml: 1.5,
                        pl: 0.75,
                        borderLeft: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {section.items
                        .filter((item) => item.to !== dashboardItem?.to)
                        .map(({ to, label, icon: Icon, soon }) => {
                          const active = activeTo === to;
                          return (
                            <ListItem
                              key={to}
                              disablePadding
                              sx={{ display: 'block', mb: 0.25 }}
                            >
                              <ListItemButton
                                component={RouterLink}
                                to={to}
                                selected={active}
                                sx={{
                                  position: 'relative',
                                  minHeight: 38,
                                  borderRadius: 1.5,
                                  px: 1.25,
                                  '&.Mui-selected': {
                                    bgcolor: 'action.selected',
                                    '&:hover': { bgcolor: 'action.selected' },
                                    '&::before': {
                                      content: '""',
                                      position: 'absolute',
                                      left: 0,
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      width: 3,
                                      height: '60%',
                                      borderRadius: 2,
                                      bgcolor: 'secondary.main',
                                    },
                                  },
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: 0,
                                    mr: 1.5,
                                    justifyContent: 'center',
                                    color: active
                                      ? 'secondary.main'
                                      : 'text.secondary',
                                  }}
                                >
                                  <Icon size={18} />
                                </ListItemIcon>
                                <ListItemText primary={label} />
                                {soon && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      px: 0.75,
                                      py: 0.1,
                                      borderRadius: 1,
                                      bgcolor: 'action.hover',
                                      color: 'text.secondary',
                                      fontSize: 10,
                                      fontWeight: 600,
                                    }}
                                  >
                                    Soon
                                  </Typography>
                                )}
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                    </Box>
                  </Collapse>
                </List>
              );
            })}
          </Box>

          <Popover
            open={Boolean(collapsedMenuAnchor && collapsedSection)}
            anchorEl={collapsedMenuAnchor}
            onClose={() => {
              setCollapsedMenuAnchor(null);
              setCollapsedMenuSection(null);
            }}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
              paper: {
                sx: {
                  ml: 1,
                  minWidth: 230,
                  border: '1px solid',
                  borderColor: 'divider',
                },
              },
            }}
          >
            {collapsedSection && (
              <Box sx={{ p: 1 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ px: 1, py: 0.75, color: 'text.secondary' }}
                >
                  {CollapsedSectionIcon && <CollapsedSectionIcon size={16} />}
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.6,
                    }}
                  >
                    {collapsedSection.heading}
                  </Typography>
                </Stack>
                <Divider />
                <List dense disablePadding sx={{ pt: 0.5 }}>
                  {collapsedSection.items
                    .filter((item) => item.to !== dashboardItem?.to)
                    .map(({ to, label, icon: Icon, soon }) => (
                      <ListItem key={to} disablePadding>
                        <ListItemButton
                          component={RouterLink}
                          to={to}
                          selected={activeTo === to}
                          onClick={() => {
                            setCollapsedMenuAnchor(null);
                            setCollapsedMenuSection(null);
                          }}
                          sx={{ borderRadius: 1.25, minHeight: 38, px: 1 }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: 1.25,
                              color:
                                activeTo === to
                                  ? 'secondary.main'
                                  : 'text.secondary',
                            }}
                          >
                            <Icon size={18} />
                          </ListItemIcon>
                          <ListItemText primary={label} />
                          {soon && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                              }}
                            >
                              Soon
                            </Typography>
                          )}
                        </ListItemButton>
                      </ListItem>
                    ))}
                </List>
              </Box>
            )}
          </Popover>

          <Divider />
          {environment && (
            <>
              <Tooltip
                title={`Environment: ${environment.label} — myRotra Admin v${VERSION_NUMBER}`}
                placement="right"
                disableHoverListener={open}
              >
                <Box
                  aria-label={`Environment: ${environment.label}`}
                  sx={(theme) => ({
                    minHeight: open ? 52 : 38,
                    px: open ? 1.5 : 0.5,
                    py: open ? 1 : 0.75,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: open ? 'flex-start' : 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    bgcolor: environment.color,
                    color: theme.palette.getContrastText(environment.color),
                  })}
                >
                  {open ? (
                    <>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          color: 'inherit',
                          fontWeight: 700,
                        }}
                      >
                        Environment: {environment.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{
                          color: 'inherit',
                          opacity: 0.75,
                        }}
                      >
                        myRotra Admin v{VERSION_NUMBER}
                      </Typography>
                    </>
                  ) : (
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        color: 'inherit',
                        fontSize: 10,
                        fontWeight: 700,
                      }}
                    >
                      {environment.label}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
              <Divider />
            </>
          )}
          <Box sx={{ p: open ? 1.5 : 1 }}>
            <ButtonBase
              onClick={(event) => setProfileMenuAnchor(event.currentTarget)}
              sx={{
                width: '100%',
                borderRadius: 1.5,
                justifyContent: open ? 'flex-start' : 'center',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                justifyContent={open ? 'flex-start' : 'center'}
                sx={{ p: 0.5, minWidth: 0, width: '100%' }}
              >
                <Avatar
                  src={photoUrl ?? undefined}
                  alt={account?.name}
                  sx={{
                    width: 36,
                    height: 36,
                    border: (theme) =>
                      `solid 2px ${theme.palette.background.default}`,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                  }}
                >
                  {getInitials(account?.name || '')}
                </Avatar>
                {open && (
                  <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, lineHeight: 1.2 }}
                      noWrap
                    >
                      {account?.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {account?.username}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </ButtonBase>
            <Menu
              anchorEl={profileMenuAnchor}
              open={Boolean(profileMenuAnchor)}
              onClose={() => setProfileMenuAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <MenuItem
                onClick={() => void refreshSession()}
                disabled={refreshingSession}
              >
                <ListItemIcon>
                  <RefreshCw size={17} />
                </ListItemIcon>
                {refreshingSession ? 'Refreshing session…' : 'Refresh session'}
              </MenuItem>
              <MenuItem onClick={logout}>
                <ListItemIcon>
                  <LogOut size={17} />
                </ListItemIcon>
                Log out
              </MenuItem>
            </Menu>
          </Box>
        </Drawer>

        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
          }}
        >
          <AppBar
            position="sticky"
            elevation={0}
            color="inherit"
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          >
            <Toolbar sx={{ gap: 1, minHeight: 52 }}>
              {backAction && (
                <Button
                  startIcon={<ArrowLeft size={14} />}
                  onClick={() => navigate(backAction)}
                >
                  Back
                </Button>
              )}
              {titleIcon && (
                <Box
                  sx={{
                    display: 'grid',
                    color: 'secondary.main',
                    flexShrink: 0,
                  }}
                  aria-hidden="true"
                >
                  {titleIcon}
                </Box>
              )}
              <Typography variant="h6" component="h1" sx={{ flexGrow: 1 }}>
                {title}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                {actions}
              </Stack>
            </Toolbar>
          </AppBar>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: { xs: 1.5, md: 2.5 },
              minWidth: 0,
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
